import React, { useReducer, useEffect, useCallback, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectCartItems, selectCartTotal, selectCartCount } from '../../features/cart/cartSlice';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { useStorefrontCurrency } from '../../contexts/StorefrontCurrencyContext';
import { useStorefrontTheme } from '../../hooks/useStorefrontTheme';
import { apiFetch } from '../../utils/api';
import CartReviewStep from './steps/CartReviewStep';
import EnquiryInfoStep from './steps/EnquiryInfoStep';
import VisitorInfoStep from './steps/VisitorInfoStep';
import AuthStep from './steps/AuthStep';
import PurchaseInfoStep from './steps/PurchaseInfoStep';
import ConfirmAddressStep from './steps/ConfirmAddressStep';
import DeliveryOptionStep from './steps/DeliveryOptionStep';
import PaymentStep from './steps/PaymentStep';
import ManualTransferStep from './steps/ManualTransferStep';
import './CheckoutModal.css';

export const STEPS = {
  CART_REVIEW: 'CART_REVIEW',
  ENQUIRY_INFO: 'ENQUIRY_INFO',
  VISITOR_INFO: 'VISITOR_INFO',
  AUTH: 'AUTH',
  PURCHASE_INFO: 'PURCHASE_INFO',
  CONFIRM_ADDRESS: 'CONFIRM_ADDRESS',
  DELIVERY_OPTIONS: 'DELIVERY_OPTIONS',
  PAYMENT: 'PAYMENT',
  MANUAL_TRANSFER: 'MANUAL_TRANSFER',  // Sprint 4/5 — Manual Bank Transfer via VT
  ORDER_CONFIRMED: 'ORDER_CONFIRMED'
};

const CHECKOUT_STEPS = [
  { key: STEPS.CART_REVIEW, label: 'Cart' },
  { key: STEPS.PURCHASE_INFO, label: 'Info' },
  { key: STEPS.CONFIRM_ADDRESS, label: 'Address' },
  { key: STEPS.DELIVERY_OPTIONS, label: 'Delivery' },
  { key: STEPS.PAYMENT, label: 'Payment' }
];

function productRequiresDelivery(product = {}) {
  const type = String(product.productType || product.type || '').toUpperCase();
  if (!type) return true;
  return type === 'PHYSICAL';
}

const initialState = {
  currentStep: STEPS.CART_REVIEW,
  stepHistory: [STEPS.CART_REVIEW],
  fullName: '',
  whatsappNumber: '',
  email: '',
  recipientType: 'myself',
  deliveryState: '',
  deliveryAddress: '',
  deliveryCity: '',
  deliveryCountry: 'Nigeria',
  shippingCalculation: null,
  deliveryMethod: 'standard',
  chowdeckFeeId: null,
  chowdeckDeliveryFee: null,
  couponCode: '',
  appliedDiscount: null,
  paymentMethod: '',
  paymentGateways: [],
  paymentCredentials: {},
  serviceChargeInfo: null,
  calculatedTotal: 0,
  completedOrder: null,
  // Manual Bank Transfer state (Sprint 4/5). Populated when the customer
  // picks Bank Transfer at Payment — carries VT details for the
  // ManualTransferStep to render + the orderId to poll.
  manualTransfer: null, // { orderId, virtualTerminal, amount, expiresAt? }
  enquiryName: '',
  enquiryPhone: '',
  enquiryEmail: '',
  enquiryNote: '',
  enquiryState: '',
  enquiryAddress: '',
  merchantStates: [],
  merchantZones: [],
  loading: false,
  error: null
};

function checkoutReducer(state, action) {
  switch (action.type) {
    case 'SET_STEP':
      return {
        ...state,
        currentStep: action.payload,
        stepHistory: [...state.stepHistory, action.payload],
        error: null
      };
    case 'GO_BACK': {
      const history = [...state.stepHistory];
      history.pop();
      const previousStep = history[history.length - 1] || STEPS.CART_REVIEW;
      return {
        ...state,
        currentStep: previousStep,
        stepHistory: history,
        error: null
      };
    }
    case 'UPDATE_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_FIELDS':
      return { ...state, ...(action.payload || action.fields) };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'RESET':
      return { ...initialState };
    default:
      return state;
  }
}

/**
 * Derive theme accent color from storeData and inject as CSS custom properties
 */
function getThemeColor(storeData) {
  const theme = storeData?.theme || storeData?.storeTheme || {};
  // Try multiple possible locations for the accent/primary color
  const color =
    theme?.primaryColor ||
    theme?.accentColor ||
    theme?.buttonColor ||
    storeData?.settings?.theme?.primaryColor ||
    storeData?.brandColor ||
    null;
  return color;
}

function hexToRgb(hex) {
  if (!hex) return null;
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return { r, g, b };
}

function darkenHex(hex, amount = 20) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const r = Math.max(0, rgb.r - amount);
  const g = Math.max(0, rgb.g - amount);
  const b = Math.max(0, rgb.b - amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// Determine if color is light (needs dark text) or dark (needs light text)
function isLightColor(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.6;
}

const CheckoutModal = ({ isOpen, onClose, storeSlug, storeData, themeSlug }) => {
  const [state, dispatch] = useReducer(checkoutReducer, initialState);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const modalRef = useRef(null);

  const cartItems = useSelector(selectCartItems);
  const cartTotal = useSelector(selectCartTotal);
  const cartCount = useSelector(selectCartCount);
  const customerAuth = useCustomerAuth();
  const { formatPrice } = useStorefrontCurrency();
  const { resolvedTheme } = useStorefrontTheme();
  const modalTheme = resolvedTheme === 'dark' ? 'dark' : 'light';
  const hasPhysicalProducts = cartItems.some((item) => productRequiresDelivery(item.product));
  const progressSteps = hasPhysicalProducts
    ? CHECKOUT_STEPS
    : CHECKOUT_STEPS.filter((step) => ![STEPS.CONFIRM_ADDRESS, STEPS.DELIVERY_OPTIONS].includes(step.key));

  // Inject theme colors as CSS custom properties on the modal element
  useEffect(() => {
    if (!modalRef.current || !isOpen) return;
    const accent = getThemeColor(storeData);
    if (accent) {
      const rgb = hexToRgb(accent);
      modalRef.current.style.setProperty('--cm-accent', accent);
      modalRef.current.style.setProperty('--cm-accent-hover', darkenHex(accent, 25));
      modalRef.current.style.setProperty('--cm-accent-text', isLightColor(accent) ? '#111827' : '#ffffff');
      if (rgb) {
        modalRef.current.style.setProperty('--cm-accent-light', `rgba(${rgb.r},${rgb.g},${rgb.b},0.08)`);
      }
    }
  }, [isOpen, storeData]);

  const goToStep = useCallback((step) => {
    dispatch({ type: 'SET_STEP', payload: step });
  }, []);

  const goBack = useCallback(() => {
    dispatch({ type: 'GO_BACK' });
  }, []);

  // Sales Tools — Phase 3.2. If the customer arrived via a checkout link
  // landing page, the storefront cart-seed dropped a one-shot prefill
  // entry in sessionStorage. Consume + clear on first open so the
  // PurchaseInfoStep starts with the merchant's pre-filled customer
  // details. Skips wrong-shape JSON cleanly.
  useEffect(() => {
    if (!isOpen) return;
    let raw = null;
    try {
      raw = sessionStorage.getItem('checkoutLinkPrefill');
    } catch {
      return;
    }
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      const info = parsed?.customerInfo;
      if (info && typeof info === 'object') {
        dispatch({
          type: 'SET_FIELDS',
          payload: {
            fullName: info.name || info.fullName || '',
            email: info.email || '',
            whatsappNumber: info.phone || info.whatsappNumber || '',
          },
        });
      }
    } catch {
      // Bad JSON — just drop it.
    } finally {
      try {
        sessionStorage.removeItem('checkoutLinkPrefill');
      } catch { /* noop */ }
    }
  }, [isOpen]);

  // Fetch store checkout data when modal opens
  useEffect(() => {
    if (!isOpen || !storeSlug) return;

    const fetchCheckoutData = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      try {
        // Correct API endpoints:
        // - Payment gateways: GET /merchant/payment-gateways/store/:storeSlug/gateways
        // - Payment credentials: GET /merchant/payment-gateways/store/:storeSlug/credentials
        // - Shipping states: GET /shipping-zones/merchant-states/:storeSlug
        // - Service charge: POST /service-charge/calculate (needs subtotal, called later)
        const [gwRaw, credRaw, statesRaw] = await Promise.all([
          apiFetch(`/merchant/payment-gateways/store/${storeSlug}/gateways`),
          apiFetch(`/merchant/payment-gateways/store/${storeSlug}/credentials`),
          apiFetch(`/shipping-zones/merchant-states/${storeSlug}`)
        ]);

        const updates = {};

        // Payment gateways — ResponseHelper wraps in { success, data, message }
        if (gwRaw.ok) {
          const gwResp = await gwRaw.json();
          const gateways = gwResp?.data || gwResp?.gateways || gwResp;
          if (Array.isArray(gateways)) {
            updates.paymentGateways = gateways;
          }
        }

        // Payment credentials
        if (credRaw.ok) {
          const credResp = await credRaw.json();
          const creds = credResp?.data || credResp?.credentials || credResp;
          if (creds && typeof creds === 'object') {
            updates.paymentCredentials = creds;
          }
        }

        // Shipping states - only use merchant-configured delivery zones
        if (statesRaw.ok) {
          const statesResp = await statesRaw.json();
          if (statesResp?.states) {
            updates.merchantStates = statesResp.states;
          }
          if (statesResp?.zones) {
            updates.merchantZones = statesResp.zones;
          }
        }

        dispatch({ type: 'SET_FIELDS', payload: updates });
      } catch (err) {
        console.error('[CheckoutModal] Failed to load checkout data:', err);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to load checkout data. Please try again.' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    fetchCheckoutData();
  }, [isOpen, storeSlug]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Warn before page reload/navigation when modal is open
  useEffect(() => {
    if (!isOpen) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      dispatch({ type: 'RESET' });
      setShowCloseConfirm(false);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (state.currentStep !== STEPS.CART_REVIEW && state.currentStep !== STEPS.ORDER_CONFIRMED) {
      setShowCloseConfirm(true);
      return;
    }
    onClose();
  }, [state.currentStep, onClose]);

  const confirmClose = useCallback(() => {
    setShowCloseConfirm(false);
    onClose();
  }, [onClose]);

  // Prevent closing on overlay click — user must use X or Back
  const handleOverlayClick = useCallback(() => {
    // Do nothing — modal should not close on overlay click
  }, []);

  const getStepTitle = () => {
    switch (state.currentStep) {
      case STEPS.CART_REVIEW: return 'Your Cart';
      case STEPS.ENQUIRY_INFO: return 'Make an Enquiry';
      case STEPS.VISITOR_INFO: return 'Your Details';
      case STEPS.AUTH: return 'Sign In';
      case STEPS.PURCHASE_INFO: return 'Checkout';
      case STEPS.CONFIRM_ADDRESS: return 'Confirm Address';
      case STEPS.DELIVERY_OPTIONS: return 'Delivery Options';
      case STEPS.PAYMENT: return 'Payment';
      case STEPS.MANUAL_TRANSFER: return 'Bank Transfer';
      case STEPS.ORDER_CONFIRMED: return 'Order Confirmed';
      default: return 'Checkout';
    }
  };

  const getProgressClass = (stepKey, index) => {
    const currentIndex = progressSteps.findIndex(s => s.key === state.currentStep);

    if (currentIndex === -1) {
      if (state.currentStep === STEPS.ORDER_CONFIRMED) return 'completed';
      return '';
    }

    if (index < currentIndex) return 'completed';
    if (index === currentIndex) return 'active';
    return '';
  };

  const commonProps = {
    state,
    dispatch,
    storeData,
    storeSlug,
    formatPrice,
    goToStep,
    goBack,
    hasPhysicalProducts,
    modalTheme
  };

  const renderStep = () => {
    switch (state.currentStep) {
      case STEPS.CART_REVIEW:
        return (
          <CartReviewStep
            {...commonProps}
            cartItems={cartItems}
            cartTotal={cartTotal}
            cartCount={cartCount}
            onClose={onClose}
          />
        );
      case STEPS.ENQUIRY_INFO:
        return <EnquiryInfoStep {...commonProps} cartItems={cartItems} cartTotal={cartTotal} />;
      case STEPS.VISITOR_INFO:
        return <VisitorInfoStep {...commonProps} cartItems={cartItems} cartTotal={cartTotal} />;
      case STEPS.AUTH:
        return <AuthStep {...commonProps} customerAuth={customerAuth} />;
      case STEPS.PURCHASE_INFO:
        return <PurchaseInfoStep {...commonProps} cartItems={cartItems} cartTotal={cartTotal} />;
      case STEPS.CONFIRM_ADDRESS:
        return <ConfirmAddressStep {...commonProps} />;
      case STEPS.DELIVERY_OPTIONS:
        return <DeliveryOptionStep {...commonProps} cartItems={cartItems} />;
      case STEPS.PAYMENT:
        return (
          <PaymentStep
            {...commonProps}
            cartItems={cartItems}
            cartTotal={cartTotal}
            onClose={onClose}
          />
        );
      case STEPS.MANUAL_TRANSFER:
        return (
          <ManualTransferStep
            {...commonProps}
            onClose={onClose}
          />
        );
      case STEPS.ORDER_CONFIRMED:
        return (
          <div className="cm-confirmed">
            <div className="cm-confirmed-icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="32" fill="#10B981" />
                <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h3 className="cm-confirmed-title">Order Confirmed!</h3>
            <p className="cm-confirmed-order">Order #{state.completedOrder?.orderNumber || 'Processing'}</p>
            <p>Thank you for your purchase. You will receive a confirmation shortly.</p>
            <button className="cm-btn-primary" onClick={onClose} style={{marginTop: 24, maxWidth: 260, margin: '24px auto 0'}}>
              Continue Shopping
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  const showProgress = ![STEPS.ORDER_CONFIRMED, STEPS.ENQUIRY_INFO, STEPS.VISITOR_INFO, STEPS.AUTH, STEPS.MANUAL_TRANSFER].includes(state.currentStep);

  return (
    <div className={`cm-overlay ${themeSlug ? `cm-overlay--${themeSlug}` : ''}`} onClick={handleOverlayClick}>
      <div className={`cm-modal ${themeSlug ? `cm-modal--${themeSlug}` : ''}`} data-cm-theme={modalTheme} ref={modalRef} onClick={e => e.stopPropagation()}>
        <div className="cm-header">
          <h2 className="cm-title">{getStepTitle()}</h2>
          <button className="cm-close-btn" onClick={handleClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {showProgress && (
          <div className="cm-progress">
            {progressSteps.map((step, i) => (
              <div
                key={step.key}
                className={`cm-progress-step ${getProgressClass(step.key, i)}`}
              >
                <div className="cm-progress-dot" />
                <span className="cm-progress-label">{step.label}</span>
              </div>
            ))}
          </div>
        )}

        <div className="cm-body">
          {renderStep()}
        </div>
      </div>

      {showCloseConfirm && (
        <div className="cm-confirm-overlay" onClick={e => e.stopPropagation()}>
          <div className="cm-confirm-dialog">
            <p>Are you sure you want to leave checkout? Your progress will be lost.</p>
            <div className="cm-confirm-actions">
              <button className="cm-confirm-cancel" onClick={() => setShowCloseConfirm(false)}>
                Continue Checkout
              </button>
              <button className="cm-confirm-leave" onClick={confirmClose}>
                Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutModal;
